/**
 * معنیِ یک ردیفِ خطِ لوله‌ی منبع — کجای راه است، و چه ویرایشی مجاز است.
 *
 * خالص و بی‌ایمپورت، پس با `npm test` آزمودنی است. و آزمودنش مهم است چون
 * هر دو چیزی که این‌جا حساب می‌شود، جای دیگری هم گفته شده و باید با آن
 * بخواند:
 *
 *   • **مرحله** — رابط رویش رنگ و فیلتر می‌گذارد. اگر تعریفش با چیزی که
 *     مایگریشن 0076 برمی‌گرداند فرق کند، جدول و فیلترش دو چیز می‌گویند.
 *   • **قاعده‌ی ویرایش** — همین قاعده‌ها در `admin_source_job()` هم اجرا
 *     می‌شوند و آن‌جا حرفِ آخر است. این نسخه فقط برای این است که آدم
 *     **قبل از** فرستادن بفهمد، نه با یک خطای پستگرس بعدش.
 *
 * ⚠️ تنها ایمپورتش `import type` است و همین هم قاعده را نمی‌شکند: تایپ
 * موقعِ کامپایل پاک می‌شود، پس این فایل همچنان در زمانِ اجرا هیچ وابستگی
 * ندارد و با `npm test` بی‌شبکه و بی‌دیتابیس آزمودنی است.
 *
 * تاکسونومی اختراع نشده: `promote_outcome` دقیقاً همان رشته‌ای است که
 * `pipeline.run_promote_pending` در مخزنِ event_crawler می‌نویسد —
 * `'promoted'` یا خودِ متنِ دلیلِ رد.
 */

import type { TestFacts } from './sourceTest';

/** یک ردیف، همان شکلی که `admin_source_pipeline` برمی‌گرداند. */
export interface PipelineRow {
  key: string;
  city: string;
  bare: string;

  draft_id: string | null;
  draft_label: string | null;
  draft_domain: string | null;
  draft_urls: string[] | null;
  draft_category: string | null;
  draft_confidence: string | null;
  discovered_at: string | null;
  discovery_run_id: number | null;

  promote_version: number | null;
  /** `'promoted'` یا متنِ دلیلِ رد. `null` یعنی هنوز حکمی نگرفته. */
  promote_outcome: string | null;
  promoted_at: string | null;

  verdict: string | null;
  listing_verdict: string | null;
  looks_usable: boolean | null;
  has_events: boolean | null;
  inspected_url: string | null;
  inspected_at: string | null;
  inspector_version: number | null;
  urls_checked: number;

  source_version: number | null;
  source_label: string | null;
  tier: string | null;
  enabled: boolean | null;
  trust: number | null;
  extractor: string | null;
  source_urls: number;
  source_at: string | null;

  /**
   * سرجمعِ عمرِ منبع.
   *
   * ⚠️ به‌تنهایی گمراه‌کننده است و برای همین `*_last` کنارش آمد: منبعی که
   * سه ماه پیش خشک شده هنوز سه هزار سند دارد و از این ستون سالم به نظر
   * می‌رسد.
   */
  documents: number;
  mentions: number;
  upcoming_events: number;
  published: number;

  /** همان سه‌تا، ولی فقط ۲۴ ساعتِ گذشته — «دیشب کار کرد؟». */
  documents_last: number;
  mentions_last: number;
  published_last: number;
  last_success_at: string | null;
  last_error: string | null;

  jobs: { id: number; kind: string; status: string; requested_at: string }[];

  /**
   * ردیفِ **بازِ** «کارِ استخراجگر» — تصمیمِ آدم، نه حدسِ ابزار.
   *
   * ⚠️ کنارِ تگِ خودکار دیده می‌شود نه به‌جایش: آن یکی می‌گوید «حدس
   * می‌زنم چه لازم است»، این یکی می‌گوید «کسی نگاه کرد و گفت کارِ کد
   * است». وقتی هر دو باشند، دومی حرفِ آخر است.
   */
  todo: SourceTodo | null;

  /**
   * آخرین تستِ **موفقِ اجرا**ی این منبع، فشرده — خوراکِ `needOf`.
   *
   * ⚠️ گزارشِ کامل این‌جا نمی‌آید و نباید بیاید: تا ۲۵۶ کیلوبایت است و
   * سیصد ردیف × آن حجم یعنی صفحه‌ای که باز نمی‌شود. فقط همان چند عددی
   * که تشخیص لازم دارد (مایگریشنِ ۰۱۰۳).
   *
   * ‏`null` یعنی هنوز تست نشده — که خودش یک وضعیتِ خواندنی است، نه نقص.
   */
  last_test: (TestFacts & { id: number; at: string | null }) | null;
}

/**
 * کجای راه.
 *
 * ⚠️ `off` و `rejected` عمداً دو چیزند و یکی کردنشان قدمِ بعدی را گم
 * می‌کند: `rejected` یعنی «غربال شد و نشد» (شاید با پارسرِ تازه بشود)، ولی
 * `off` یعنی ردیفش در رجیستری هست و کسی خاموشش گذاشته — یک کلیک تا روشن
 * شدن.
 */
export type SourceStage =
  'new' | 'inspected' | 'parked' | 'rejected' | 'off' | 'live';

/** ترتیبِ خواندن: از چیزی که تازه پیدا شده تا چیزی که دارد کار می‌کند. */
export const STAGE_ORDER: SourceStage[] =
  ['new', 'inspected', 'parked', 'rejected', 'off', 'live'];

export const STAGE_LABEL: Record<SourceStage, string> = {
  new: 'پیش‌نویس',
  inspected: 'غربال‌شده',
  parked: 'منتظرِ کد',
  rejected: 'ردشده',
  off: 'خاموش',
  live: 'فعال',
};

/**
 * تعریفِ کاملِ هر مرحله — همان چیزی که با نگه داشتنِ ماوس دیده می‌شود.
 *
 * ⚠️ **این‌جاست و نه در رابط، چون تعریفِ مرحله هم این‌جاست.** `stageOf`
 * بالاتر دقیقاً همین جمله‌ها را به شرط ترجمه می‌کند؛ اگر متن جای دیگری
 * می‌نشست، اولین باری که یک شرط عوض شود متن سرِ جایش می‌ماند و از آن روز
 * رابط چیزی می‌گوید که کد نمی‌کند.
 *
 * هر متن سه چیز دارد و باید داشته باشد: **یعنی چه**، **چطور به این‌جا
 * رسید**، و **قدمِ بعدی چیست**. سوالِ کسی که ماوس را نگه می‌دارد همیشه
 * سومی است.
 */
export const STAGE_HELP: Record<SourceStage, string> = {
  new:
    'پیش‌نویس — فقط یک نام و یک آدرس. هنوز هیچ‌کس صفحه‌اش را ندیده و '
    + 'معلوم نیست اصلاً خواندنی هست یا نه.\n\n'
    + 'چطور رسید: کشفِ خودکار پیشنهادش داد، یا خودت دستی اضافه‌اش کردی.\n\n'
    + 'قدمِ بعدی: هیچ. زنجیره‌ی شبانه هر شب تا ۲۰ پیش‌نویس را خودش غربال '
    + 'می‌کند. دکمه‌ی «بررسی» فقط برای وقتی است که عجله داری.',

  inspected:
    'غربال‌شده — صفحه‌اش گرفته شده و حکم گرفته، ولی هنوز وارد رجیستری نشده.\n\n'
    + 'دو گیتش در ستونِ «گیت‌ها» دیده می‌شود: «استخراج» یعنی با خواننده‌های '
    + 'امروزِ ما خواندنی است، «رویداد» یعنی رخدادِ پیشِ‌رو دارد و بایگانی نیست.\n\n'
    + 'قدمِ بعدی: اگر هر دو ✅ باشند، ترفیعِ شبانه خودش برش می‌دارد. دکمه‌ی '
    + '«ترفیع» فقط میان‌بر است.',

  rejected:
    'ردشده — ترفیع نگاهش کرد و نه گفت. دلیلش زیرِ ردیف نوشته شده.\n\n'
    + '⚠️ یعنی «امروز نشد»، نه «هیچ‌وقت نمی‌شود». دو دلیلِ ممکن: با '
    + 'extractorهای امروز خوانده نمی‌شود، یا رخدادِ پیشِ‌رو ندارد و احتمالاً '
    + 'بایگانی است.\n\n'
    + 'قدمِ بعدی: آدرسش را در «ویرایشِ پیش‌نویس» درست کن — حکم خودکار پاک '
    + 'می‌شود و دوباره سنجیده می‌شود. و اگر واقعاً بی‌ربط است، دستی ردش '
    + 'بگذار تا از صف بیرون بماند.',

  off:
    'خاموش — ردیفِ واقعی در رجیستری دارد، ولی اجرا نمی‌شود.\n\n'
    + '⚠️ با «ردشده» یکی نیست، و همین مهم‌ترین تفاوتِ این جدول است: ردشده '
    + 'یعنی هیچ‌وقت وارد نشد؛ خاموش یعنی وارد شده بود و کسی خاموشش کرده — '
    + 'یا tierش disallowed است.\n\n'
    + 'قدمِ بعدی: یک کلیک تا روشن شدن. تاریخچه و تنظیماتش دست‌نخورده مانده‌اند.',

  parked:
    'منتظرِ کد — کسی این ردیف را فرستاده سراغِ «کارِ استخراجگر».\n\n'
    + 'یعنی تکلیفش روشن است و کاری از تو برنمی‌آید تا وقتی آن کد نوشته '
    + 'شود. برای همین هم از «پیش‌نویس» بیرون آمده: آن فهرست جای چیزهایی '
    + 'است که منتظرِ یک کلیک‌اند.\n\n'
    + 'قدمِ بعدی: در پنلِ ردیف، «کد نوشته شد» برش می‌گرداند — و آن وقت '
    + 'دوباره غربال و ترفیع می‌شود.',

  live:
    'فعال — هر شب گرفته می‌شود و رخدادهایش به اپ می‌رسند.\n\n'
    + '⚠️ «فعال» یعنی اجرا می‌شود، نه اینکه سالم است. ستونِ «۲۴س / کل» را '
    + 'نگاه کن: صفرِ سمتِ چپ کنارِ سرجمعِ بزرگ یعنی منبعی که خشک شده و هنوز '
    + 'فعال به نظر می‌رسد.\n\n'
    + 'قدمِ بعدی: اگر چیزی درست نیست، «تست» می‌گوید کجای شش قدم می‌شکند.',
};

export function stageOf(row: PipelineRow): SourceStage {
  // ردیفِ رجیستری هر چیزِ دیگری را می‌پوشاند: پیش‌نویسش هرچه بوده، امروز
  // این منبع وجود دارد.
  if (row.source_version != null) {
    return row.enabled && row.tier !== 'disallowed' ? 'live' : 'off';
  }
  /**
   * ⚠️ صفِ استخراجگر **بر همه‌ی حالت‌های پیش‌نویس مقدم است.**
   *
   * وقتی کسی ردیفی را فرستاده سراغِ کد، آن ردیف دیگر «منتظرِ یک کلیک»
   * نیست — منتظرِ نوشته شدنِ چیزی است. ماندنش در «پیش‌نویس» یعنی هر بار
   * که آن فهرست را باز می‌کنی، ردیفی را می‌بینی که تکلیفش از قبل روشن
   * شده و کاری از تو برنمی‌آید.
   *
   * بر «غربال‌شده» و «ردشده» هم مقدم است، به همان دلیل: هر دو حالتی‌اند
   * که منتظرِ تصمیمِ آدم‌اند، و تصمیم گرفته شده.
   *
   * ⚠️ ولی بر رجیستری مقدم **نیست** (بندِ بالا): منبعی که امروز اجرا
   * می‌شود، هرچقدر هم که کدش بهتر شود، «فعال» است.
   */
  if (row.todo) return 'parked';
  if (row.promote_outcome != null && row.promote_outcome !== 'promoted') return 'rejected';
  if (row.inspected_at != null) return 'inspected';
  return 'new';
}

/**
 * چرا این ردیف ترفیع نگرفت — یا `null` اگر گرفت/هنوز حکمی ندارد.
 *
 * متن از خودِ کرالر می‌آید و این‌جا ترجمه نمی‌شود: دو نگارشِ فارسی از یک
 * دلیل یعنی جستجوی «چرا این رد شد» در دو مخزن دو جواب می‌دهد.
 */
export function rejectionReason(row: PipelineRow): string | null {
  const outcome = row.promote_outcome;
  if (!outcome || outcome === 'promoted') return null;
  return outcome;
}

/**
 * دو گیتِ ترفیع، به‌شکلی که در جدول دیده شود.
 *
 * همان دو تا که `inspector/promote.py` دارد، و ترتیبشان هم همان: بدونِ
 * `looks_usable` پارسر لازم است، و بدونِ `has_events` صفحه احتمالاً
 * بایگانی است — که «سالم به نظر می‌رسد و صفر mention می‌دهد».
 */
export function gates(row: PipelineRow): { usable: boolean | null; events: boolean | null } {
  return { usable: row.looks_usable, events: row.has_events };
}

/** `https://www.NAC-cna.ca/en/events?x=1` → `nac-cna.ca`. قرینه‌ی `crawler_url_host` در 0076. */
export function hostOf(url: string): string {
  const trimmed = (url ?? '').trim().toLowerCase();
  const withoutScheme = trimmed.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
  const withoutUser = withoutScheme.replace(/^[^@/]*@/, '');
  const host = withoutUser.split(/[/:?#]/)[0] ?? '';
  return host.replace(/^www\./, '');
}

/**
 * فیلدهایی که از صفحه‌ی ادمین قابلِ ویرایش‌اند.
 *
 * فهرستِ **بسته** است و نه سیاه: فیلدی که فردا به `Source` اضافه شود نباید
 * خودبه‌خود از این‌جا نوشتنی بشود. عینِ همین فهرست در `admin_source_job()`
 * هم هست و آن‌جا حرفِ آخر است.
 */
export const EDITABLE_FIELDS = [
  'label', 'trust', 'notes', 'rationale', 'urls', 'tier', 'enabled',
  'extractor', 'timezone', 'goout_source', 'follow', 'follow_domains',
  'paginate', 'aggregator', 'heuristic_detail', 'default_categories',
  'default_venue', 'default_venue_address', 'image_rewrite', 'options',
  'expect', 'min_interval_seconds', 'timeout_seconds',
] as const;

export type EditableField = (typeof EDITABLE_FIELDS)[number];

/**
 * فیلدهایی که «رفتار» را می‌سازند — تنها چیزهایی که نسخه‌ی تازه توجیه
 * می‌کنند.
 *
 * ⚠️ **کپیِ دقیقِ `BEHAVIOR_FIELDS` در `storage/registry_db.py` مخزنِ
 * event_crawler است و باید بماند.** آن‌جا حرفِ آخر است: هشِ رفتاری از روی
 * همین فهرست ساخته می‌شود و `put()` با مقایسه‌ی همان هش تصمیم می‌گیرد
 * نسخه‌ی تازه بسازد یا نه. این نسخه فقط برای این است که رابط **قبل از**
 * ثبت بگوید چه اتفاقی می‌افتد.
 *
 * معیارِ عضویت — که سلیقه‌ای نیست — «آیا عوض شدنش دادهٔ بیرون‌آمده را عوض
 * می‌کند؟» است، نه «آیا تنظیم است؟». برای همین `trust` بیرون است
 * (اولویتِ fusion است، یک mention را جابه‌جا نمی‌کند) و `notes` هم بیرون
 * است: متنش تاریخِ روز را دارد، و اگر می‌شمرد هر اجرای `inspect` یک نسخه‌ی
 * تازه می‌ساخت و جدولِ نسخه‌ها به تاریخچه‌ی *اجراها* تبدیل می‌شد نه
 * تغییرها.
 */
export const BEHAVIOR_FIELDS = [
  'urls', 'extractor', 'tier', 'enabled', 'city', 'timezone', 'goout_source',
  'follow', 'follow_domains', 'paginate', 'aggregator', 'heuristic_detail',
  'default_categories', 'default_venue', 'default_venue_address',
  'image_rewrite', 'options',
] as const;

/**
 * آیا این ویرایش نسخه‌ی تازه می‌سازد؟
 *
 * ⚠️ «نه» یعنی ردیفِ فعال **همان‌جا** به‌روز می‌شود، نه اینکه چیزی نشود —
 * فرقِ این دو همان جایی است که یک ویرایشِ برچسب می‌توانست بی‌صدا گم شود
 * (نگاه کن به plan/prompts/crawler-admin-jobs.md).
 */
export function makesNewVersion(patch: Record<string, unknown>): boolean {
  return Object.keys(patch).some(
    (key) => (BEHAVIOR_FIELDS as readonly string[]).includes(key),
  );
}

/** جوابِ اعتبارسنجی: یا خطا دارد یا ندارد. متنِ خطا مستقیم نشان داده می‌شود. */
export interface EditCheck { ok: boolean; error?: string }

/**
 * آیا این ویرایش مجاز است؟
 *
 * سه قاعده، و هر سه تصمیمِ ۱۳ اوت‌اند نه محدودیتِ فنی:
 *
 *   ۱. **شهر عوض نمی‌شود.** شهر کلیدِ join کلِ خطِ لوله است — سند و
 *      mention و رخدادِ دیروز زیرِ شهرِ قبلی می‌مانند و ردیف از فردا زیرِ
 *      شهرِ تازه می‌رود. دو نیمه‌ی ناسازگار، بی‌هیچ خطایی.
 *   ۲. **دامنه عوض نمی‌شود.** آدرس آزاد است ولی میزبانش باید یکی از
 *      میزبان‌های فعلی باشد. سایتِ دیگر یعنی منبعِ دیگر.
 *   ۳. **منبعِ `enabled` شهر می‌خواهد.** این یکی فنی است و در پایتون
 *      اجرا می‌شود (`_enabled_source_needs_a_city`)؛ گفتنش این‌جا یعنی
 *      آدم همان لحظه می‌فهمد، نه فردا از روی یک کارِ شکست‌خورده.
 */
export function validateEdit(
  current: Record<string, unknown>,
  patch: Record<string, unknown>,
): EditCheck {
  const keys = Object.keys(patch);
  if (keys.length === 0) return { ok: false, error: 'چیزی عوض نشده.' };

  for (const key of keys) {
    if (key === 'city' || key === 'id') {
      return { ok: false, error: 'شهر و شناسه از این‌جا عوض نمی‌شوند.' };
    }
    if (!(EDITABLE_FIELDS as readonly string[]).includes(key)) {
      return { ok: false, error: `فیلدِ «${key}» از این‌جا قابلِ ویرایش نیست.` };
    }
  }

  if ('urls' in patch) {
    const next = patch.urls;
    if (!Array.isArray(next) || next.length === 0) {
      return { ok: false, error: 'دست‌کم یک آدرس لازم است.' };
    }
    const allowed = new Set(
      (Array.isArray(current.urls) ? (current.urls as string[]) : []).map(hostOf),
    );
    for (const url of next as string[]) {
      if (!/^https?:\/\//i.test(url)) {
        return { ok: false, error: `آدرس باید با http:// یا https:// شروع شود — «${url}»` };
      }
      const host = hostOf(url);
      if (!allowed.has(host)) {
        return { ok: false, error: `دامنه‌ی «${host}» مالِ این منبع نیست؛ دامنه عوض نمی‌شود.` };
      }
    }
  }

  if (patch.enabled === true && !current.city) {
    return { ok: false, error: 'منبعِ فعال باید شهر داشته باشد؛ این یکی ندارد.' };
  }

  return { ok: true };
}

/**
 * فقط چیزهایی که واقعاً فرق دارند.
 *
 * ⚠️ بدونِ این، فرمِ ویرایش هر بار **همه‌ی** فیلدها را می‌فرستد و کارِ
 * صف‌شده چیزی می‌شود که نمی‌شود خواندش: «چه چیزی قرار است عوض شود؟» باید
 * از خودِ ردیفِ صف معلوم باشد، نه با مقایسه‌ی دستی.
 */
export function changedFields(
  current: Record<string, unknown>,
  next: Record<string, unknown>,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(next)) {
    if (JSON.stringify(value) !== JSON.stringify(current[key] ?? null)) patch[key] = value;
  }
  return patch;
}

/** خلاصه‌ی یک کارِ در صف، برای نشانِ کنارِ ردیف. */
export const JOB_LABEL: Record<string, string> = {
  discover: 'کشف',
  inspect: 'بررسی',
  promote: 'ترفیع',
  edit_source: 'ویرایش',
  add_source: 'منبعِ تازه',
};

/**
 * پیشوندِ حکمِ دستی — تنها چیزی که «آدم گفت نه» را از «کرالر گفت نه» جدا
 * می‌کند.
 *
 * ⚠️ لازم است، چون هر دو در یک ستون می‌نشینند (`promote_outcome`). بدونِ
 * آن، فهرستِ «دلایلِ قبلی» متن‌های ماشین را هم پیشنهاد می‌داد — یعنی آدم
 * می‌توانست «با extractorهای امروز خوانده نمی‌شود» را دستی ثبت کند بی‌آنکه
 * چیزی امتحان شده باشد. آن وقت دلیلی که در جدول می‌خوانی، ادعای کسی است
 * که آن را نسنجیده.
 */
export const MANUAL_REJECT_PREFIX = 'دستی: ';

/**
 * دلایلِ آماده. فهرست **بسته نیست** — تایپ کردنِ متنِ تازه همیشه ممکن
 * است و همان هم به فهرستِ دفعه‌ی بعد اضافه می‌شود.
 */
export const MANUAL_REJECT_REASONS: string[] = [
  'به این پروژه نمی‌خورد',
  'رخداد ندارد — سایتِ خبری یا معرفی است',
  'شهرش اشتباه است',
  'تکراری — همین منبع با شناسه‌ی دیگری هست',
  'سایت تعطیل شده',
  'فقط رخدادِ گذشته دارد',
];

/** این حکم را آدم نوشته یا کرالر؟ */
export function isManualRejection(outcome: string | null | undefined): boolean {
  return typeof outcome === 'string' && outcome.startsWith(MANUAL_REJECT_PREFIX);
}

/**
 * فهرستِ پیشنهادها: آماده‌ها، به‌علاوه‌ی هر دلیلِ دستی که قبلاً استفاده
 * شده — بی‌تکرار، و پیشوند از سرشان برداشته.
 *
 * ⚠️ دلایلِ قبلی **بالاتر** می‌آیند: کسی که یک بار متنی نوشته، احتمالاً
 * همان را دوباره می‌خواهد. آماده‌ها برای دفعه‌ی اول‌اند.
 */
export function rejectSuggestions(seen: (string | null | undefined)[]): string[] {
  const used = seen
    .filter(isManualRejection)
    .map((o) => (o as string).slice(MANUAL_REJECT_PREFIX.length).trim())
    .filter(Boolean);
  return [...new Set([...used, ...MANUAL_REJECT_REASONS])];
}


/**
 * خواننده‌هایی که می‌شود انتخاب کرد.
 *
 * ⚠️ **این فهرست باید با `EXTRACTORS` در مخزنِ event_crawler بخواند.**
 * نامی که این‌جا باشد و آن‌جا نه، یک منبعِ خاموشِ بی‌خطا می‌سازد: کرالر
 * `unsupported-extractor` می‌شمارد و رد می‌شود، و رابط هیچ‌وقت نمی‌گوید
 * چرا. برای همین هم ورودیِ متنیِ آزاد از فرم‌ها برداشته شد.
 *
 * ترتیب همان «نردبانِ ترجیح» است که بالای `extractors/__init__.py` نوشته
 * شده: هرچه بالاتر، پایدارتر و ارزان‌تر.
 */
export const EXTRACTOR_CHOICES: { value: string; label: string }[] = [
  { value: 'jsonld', label: 'jsonld — دادهٔ استانداردِ داخلِ صفحه' },
  { value: 'cards', label: 'cards — کارت‌های تکرارشونده‌ی HTML' },
  { value: 'ics', label: 'ics — فایلِ تقویم' },
  { value: 'detail', label: 'detail — فقط صفحه‌ی خودِ رخداد' },
  { value: 'ticketmaster', label: 'ticketmaster — اختصاصی' },
  { value: 'cal613', label: 'cal613 — اختصاصی' },
  { value: 'opl', label: 'opl — اختصاصی' },
  { value: 'ottawa_tourism', label: 'ottawa_tourism — اختصاصی' },
  /**
   * ⚠️ `none` **خواننده نیست** و در `EXTRACTORS` وجود ندارد. کرالر نامِ
   * ناشناخته را `unsupported-extractor` می‌شمارد و ردش می‌کند — که
   * اتفاقاً همان «فقط سند جمع کن» را می‌دهد.
   *
   * این‌جا آمده چون هشت منبع از قبل رویش تنظیم‌اند و برداشتنش از فهرست
   * یعنی فرم مقدارِ فعلیِ آن‌ها را نشان ندهد. ولی رفتارِ تصادفی است، نه
   * طراحی — و روزی یا باید خواننده‌ی واقعی بشود یا از رجیستری برود.
   */
  { value: 'none', label: 'none — خواننده‌ای اجرا نکن (فقط سند)' },
];

/**
 * منطقه‌های زمانی‌ای که واقعاً استفاده می‌شوند.
 *
 * ⚠️ فهرستِ کاملِ IANA چهارصد ردیف است و انتخاب از آن سخت‌تر از تایپ
 * کردنش. این‌ها همان‌هایی‌اند که شهرهای فهرستِ اپ لازم دارند — و یک
 * غلطِ املایی در این فیلد یعنی رخدادهایی که چند ساعت جابه‌جا منتشر
 * می‌شوند، بی‌آنکه چیزی خطا بدهد.
 */
export const TIMEZONE_CHOICES: string[] = [
  'America/Toronto',
  'America/Halifax',
  'America/St_Johns',
  'America/Winnipeg',
  'America/Regina',
  'America/Edmonton',
  'America/Vancouver',
  'Europe/London',
  'Europe/Stockholm',
  'Asia/Dubai',
  'Asia/Tehran',
];


// ═══════════ کارِ استخراجگر ═══════════

/**
 * دو نوع، چون دو کارِ متفاوت‌اند — و شمارشِ جداشان همان چیزی است که
 * می‌گوید کدام یکی به‌صرفه‌تر است.
 *
 * ⚠️ `fix-extractor` معمولاً ارزش‌بیشتری دارد: `cards` خودش می‌گوید ۱۸ از
 * ۱۹ منبعِ backlog را می‌خواند، پس یک اصلاح در آن ده‌ها منبع را با هم
 * درست می‌کند. `new-extractor` همیشه فقط یک سایت را حل می‌کند.
 */
export type TodoKind = 'new-extractor' | 'fix-extractor';

export interface SourceTodo {
  id: number;
  kind: TodoKind;
  note: string | null;
  /** وضعیتِ صفحه در لحظه‌ی ثبت — تا شش ماه بعد معلوم باشد چرا این‌جاست. */
  evidence: Record<string, unknown>;
  at: string;
}

export const TODO_LABEL: Record<TodoKind, string> = {
  'new-extractor': 'پارسرِ تازه لازم است',
  'fix-extractor': 'استخراجگر باید اصلاح شود',
};

export const TODO_HELP: Record<TodoKind, string> = {
  'new-extractor':
    'هیچ‌کدام از خواننده‌های موجود این سایت را نمی‌خوانند — یک فایلِ تازه '
    + 'در `extractors/` لازم است.\n\n'
    + '⚠️ فقط یک سایت را حل می‌کند. قبل از انتخابش مطمئن شو که «اصلاحِ '
    + 'استخراجگر» جوابش نیست: یک اصلاح در `cards` ده‌ها منبع را با هم '
    + 'درست می‌کند.',
  'fix-extractor':
    'خواننده‌ای هست که این سایت را می‌بیند، ولی ناقص می‌خواند — عنوان '
    + 'می‌آید و زمان نه، یا نصفِ کارت‌ها رد می‌شوند.\n\n'
    + 'معمولاً به‌صرفه‌ترین کار است: `cards` خودش می‌گوید ۱۸ از ۱۹ منبعِ '
    + 'backlog را می‌خواند، پس هر اصلاحی در آن روی همه‌شان می‌نشیند.',
};

/**
 * یادداشت‌های آماده. فهرست **بسته نیست** — تایپ کردن همیشه ممکن است و
 * همان هم به فهرستِ دفعه‌ی بعد اضافه می‌شود، مثلِ دلایلِ رد.
 */
export const TODO_NOTES: Record<TodoKind, string[]> = {
  'new-extractor': [
    'ساختارِ استاندارد ندارد و کارت هم نیست',
    'محتوا با جاوااسکریپت ساخته می‌شود',
    'API پشتش هست، باید صدایش زد',
    'قالبِ تاریخش هیچ‌کدام از الگوهای فعلی نیست',
  ],
  'fix-extractor': [
    'cards عنوان می‌دهد ولی زمان نه',
    'cards نصفِ کارت‌ها را رد می‌کند',
    'تاریخِ متنی خوانده نمی‌شود',
    'صفحه‌ی دوم قالبش فرق دارد',
    'عنوان از تیترِ بخش برداشته می‌شود، نه از کارت',
  ],
};

/** یادداشت‌های آماده + هرچه قبلاً نوشته شده، بی‌تکرار. */
export function todoNoteSuggestions(
  kind: TodoKind, seen: (string | null | undefined)[],
): string[] {
  const used = seen.filter((n): n is string => Boolean(n && n.trim()));
  return [...new Set([...used, ...TODO_NOTES[kind]])];
}
